if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        this.personalizationInput =
          this.form.querySelector('[data-personalization-input]') ||
          document.querySelector(`[data-personalization-input][form="${this.form.id}"]`) ||
          document.querySelector('[data-personalization-input]');
        if (this.personalizationInput) {
          this.personalizationInput.addEventListener('input', () => this.clearPersonalizationError());
        }
      }

      getGiftWrapCheckbox() {
        return (
          this.querySelector('.gift-wrap-option__checkbox') ||
          document.querySelector(`.gift-wrap-option__checkbox[data-form-id="${this.form.id}"]`) ||
          document.querySelector('.gift-wrap-option__checkbox')
        );
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        if (!this.validatePersonalization()) return;
        if (!this.validatePhotoPersonalization()) return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const giftWrapCheckbox = this.getGiftWrapCheckbox();
        const giftWrapChecked = giftWrapCheckbox && giftWrapCheckbox.checked;

        const formData = new FormData(this.form);
        // Personalization is rendered as a separate block (outside the form), so make
        // sure its line item property is always included, regardless of form association.
        if (
          this.personalizationInput &&
          this.personalizationInput.name &&
          !formData.has(this.personalizationInput.name)
        ) {
          formData.append(this.personalizationInput.name, this.personalizationInput.value);
        }
        // Photo personalization hidden URL properties (also outside the form tree).
        this.appendPhotoPersonalizationProperties(formData);
        this.stripEmptyPhotoPersonalizationProperties(formData);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        if (giftWrapChecked) {
          const giftWrapVariantId = giftWrapCheckbox.dataset.giftWrapVariant;
          const variantId = formData.get('id');
          const quantity = formData.get('quantity') || 1;
          const sections = this.cart
            ? this.cart
                .getSectionsToRender()
                .map((s) => s.id)
                .join(',')
            : '';

          // Preserve any line item properties (e.g. personalization) on the main item.
          const properties = {};
          formData.forEach((value, key) => {
            const match = key.match(/^properties\[(.*)\]$/);
            if (match) properties[match[1]] = value;
          });
          const mainItem = { id: parseInt(variantId), quantity: parseInt(quantity) };
          if (Object.keys(properties).length > 0) mainItem.properties = properties;

          config.headers['Content-Type'] = 'application/json';
          config.body = JSON.stringify({
            items: [
              mainItem,
              { id: parseInt(giftWrapVariantId), quantity: parseInt(quantity), parent_id: parseInt(variantId) },
            ],
            sections: sections,
            sections_url: window.location.pathname,
          });
        } else {
          config.body = formData;
        }

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure('add:paint-updated-sections', () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure('add:paint-updated-sections', () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent('add:user-action', evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      validatePersonalization() {
        if (!this.personalizationInput || !this.personalizationInput.hasAttribute('required')) return true;

        if (this.personalizationInput.value.trim() !== '') {
          this.clearPersonalizationError();
          return true;
        }

        const errorEl = this.getPersonalizationError();
        if (errorEl) errorEl.removeAttribute('hidden');
        this.personalizationInput.setAttribute('aria-invalid', 'true');
        this.personalizationInput.focus();
        return false;
      }

      getPersonalizationError() {
        if (!this.personalizationInput) return null;
        const field = this.personalizationInput.closest('[data-personalization]');
        return (
          (field && field.querySelector('[data-personalization-error]')) ||
          document.querySelector('[data-personalization-error]')
        );
      }

      clearPersonalizationError() {
        if (!this.personalizationInput) return;
        const errorEl = this.getPersonalizationError();
        if (errorEl) errorEl.setAttribute('hidden', '');
        this.personalizationInput.removeAttribute('aria-invalid');
      }

      getPhotoPersonalization() {
        return (
          this.querySelector('photo-personalization') ||
          document.querySelector(
            `photo-personalization[data-product-form-id="${this.form && this.form.id}"]`
          ) ||
          document.querySelector('photo-personalization')
        );
      }

      validatePhotoPersonalization() {
        const photoField = this.getPhotoPersonalization();
        if (!photoField || typeof photoField.validateForCart !== 'function') return true;
        return photoField.validateForCart();
      }

      appendPhotoPersonalizationProperties(formData) {
        const photoField = this.getPhotoPersonalization();
        if (!photoField) return;

        photoField.querySelectorAll('[data-photo-url-input], [data-photo-sent-input]').forEach((input) => {
          if (!input.name || formData.has(input.name)) return;
          if ((input.value || '').trim() === '') return;
          formData.append(input.name, input.value);
        });
      }

      stripEmptyPhotoPersonalizationProperties(formData) {
        Array.from(formData.keys()).forEach((key) => {
          if (!/^properties\[_foto_/.test(key)) return;
          if (String(formData.get(key) || '').trim() !== '') return;
          formData.delete(key);
        });
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
